import { useState } from 'react';
import { useCart } from '../../../context/CartContext';
import PaymentSelectModal from '../../../components/feature/PaymentSelectModal';
import ProductReviews from './ProductReviews';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  category: string;
  description: string;
  rating: number;
  reviews: number;
  discount?: number;
  stock: number;
}

interface ProductModalProps {
  product: Product;
  onClose: () => void;
}

type ModalTab = 'info' | 'reviews';

export default function ProductModal({ product, onClose }: ProductModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPaymentSelect, setShowPaymentSelect] = useState(false);
  const [activeTab, setActiveTab] = useState<ModalTab>('info');
  const { addToCart } = useCart();

  const formatPrice = (price: number) => price.toLocaleString('ko-KR');

  const handleAddToCart = () => {
    addToCart(
      {
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.image,
        category: product.category,
      },
      quantity,
    );
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white w-full md:max-w-4xl md:rounded-2xl rounded-t-2xl max-h-[94vh] md:max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* 모바일 핸들 */}
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors cursor-pointer z-10"
          aria-label="닫기"
        >
          <i className="ri-close-line text-lg md:text-xl text-gray-600 w-5 h-5 flex items-center justify-center" />
        </button>

        {/* 상단: 이미지 + 핵심 정보 */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-8 p-4 md:p-8 pb-0 md:pb-0">
          {/* 제품 이미지 */}
          <div className="w-full h-56 md:h-80 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* 제품 핵심 정보 */}
          <div className="flex flex-col">
            <div className="mb-3">
              <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full mb-2">
                {product.category}
              </span>
              <h2
                className="text-xl md:text-3xl font-bold text-gray-900 mb-2 leading-snug"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
              >
                {product.name}
              </h2>

              {/* 별점 — 클릭하면 리뷰 탭으로 이동 */}
              <button
                type="button"
                onClick={() => setActiveTab('reviews')}
                className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`${
                        i < Math.floor(product.rating) ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'
                      } text-sm w-4 h-4 flex items-center justify-center`}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-600 underline underline-offset-2">
                  {product.rating} ({product.reviews}개 리뷰)
                </span>
              </button>
            </div>

            {/* 가격 */}
            <div className="mb-4">
              <div className="flex items-baseline gap-2 mb-1">
                {product.originalPrice && (
                  <span className="text-sm md:text-base text-gray-400 line-through">
                    {formatPrice(product.originalPrice)}원
                  </span>
                )}
                {product.discount && (
                  <span className="text-base font-bold text-red-500">{product.discount}%</span>
                )}
              </div>
              <p className="text-2xl md:text-3xl font-bold text-emerald-600">
                {formatPrice(product.price)}원
              </p>
            </div>

            {/* 재고 / 배송 */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg grid grid-cols-2 gap-y-2">
              <span className="text-xs text-gray-500">재고</span>
              <span className="text-xs font-semibold text-gray-900 text-right">{product.stock}개 남음</span>
              <span className="text-xs text-gray-500">배송</span>
              <span className="text-xs font-semibold text-emerald-600 text-right">무료배송</span>
            </div>

            {/* 수량 선택 */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-900 mb-2">수량</label>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-9 h-9 flex items-center justify-center text-gray-600 hover:text-emerald-600 transition-colors cursor-pointer"
                    aria-label="수량 감소"
                  >
                    <i className="ri-subtract-line text-lg w-5 h-5 flex items-center justify-center" />
                  </button>
                  <span className="w-10 text-center font-bold text-gray-900 text-sm">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="w-9 h-9 flex items-center justify-center text-gray-600 hover:text-emerald-600 transition-colors cursor-pointer"
                    aria-label="수량 증가"
                  >
                    <i className="ri-add-line text-lg w-5 h-5 flex items-center justify-center" />
                  </button>
                </div>
                <div className="flex-1 text-right">
                  <p className="text-xs text-gray-500 mb-0.5">총 금액</p>
                  <p className="text-xl font-bold text-emerald-600">{formatPrice(product.price * quantity)}원</p>
                </div>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 mt-auto">
              <button
                onClick={handleAddToCart}
                className="flex-1 py-3 bg-emerald-600 text-white rounded-full text-sm font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                장바구니 담기
              </button>
              <button
                onClick={() => setShowPaymentSelect(true)}
                className="flex-1 py-3 bg-gray-900 text-white rounded-full text-sm font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5"
              >
                <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center" />
                바로 구매
              </button>
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="flex gap-0 mx-4 md:mx-8 mt-5 md:mt-6 border-b border-gray-200">
          {(['info', 'reviews'] as ModalTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab === 'info' ? (
                <>
                  <i className="ri-file-text-line w-4 h-4 flex items-center justify-center" />
                  제품 상세
                </>
              ) : (
                <>
                  <i className="ri-star-line w-4 h-4 flex items-center justify-center" />
                  리뷰 &amp; 별점
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 bg-emerald-100 text-emerald-700 text-[10px] font-black rounded-full">
                    {product.reviews}
                  </span>
                </>
              )}
            </button>
          ))}
        </div>

        {/* 탭 콘텐츠 */}
        {activeTab === 'info' && (
          <div className="px-4 md:px-8 py-5 md:py-6">
            <h3 className="text-sm font-bold text-gray-900 mb-3">제품 설명</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">{product.description}</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { icon: 'ri-leaf-line', label: '100% 유기농 인증', color: 'text-emerald-600 bg-emerald-50' },
                { icon: 'ri-shield-check-line', label: 'HACCP 인증 시설', color: 'text-teal-600 bg-teal-50' },
                { icon: 'ri-truck-line', label: '신선 당일 포장', color: 'text-sky-600 bg-sky-50' },
                { icon: 'ri-heart-pulse-line', label: '영양 성분 검증', color: 'text-rose-600 bg-rose-50' },
                { icon: 'ri-recycle-line', label: '친환경 패키징', color: 'text-green-600 bg-green-50' },
                { icon: 'ri-customer-service-2-line', label: '1:1 고객 상담', color: 'text-amber-600 bg-amber-50' },
              ].map((item) => (
                <div key={item.label} className={`flex items-center gap-2.5 p-3 rounded-xl ${item.color.split(' ')[1]}`}>
                  <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${item.color.split(' ')[0]}`}>
                    <i className={`${item.icon} text-base w-4 h-4 flex items-center justify-center`} />
                  </div>
                  <span className="text-xs font-semibold text-gray-800 leading-tight">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <p className="text-xs font-bold text-amber-800 mb-1 flex items-center gap-1.5">
                <i className="ri-information-line w-3.5 h-3.5 flex items-center justify-center" />
                섭취 방법
              </p>
              <p className="text-xs text-amber-700 leading-relaxed">
                1일 2회, 1회 섭취 시 식전 또는 식후에 물과 함께 복용하세요. 
                개인에 따라 섭취량을 조절하시기 바랍니다.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <ProductReviews
            productId={product.id}
            productName={product.name}
            baseRating={product.rating}
            baseReviewCount={product.reviews}
          />
        )}

        {/* 장바구니 성공 오버레이 */}
        {showSuccess && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-t-2xl md:rounded-2xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center bg-emerald-100 rounded-full">
                <i className="ri-check-line text-3xl text-emerald-600 w-8 h-8 flex items-center justify-center" />
              </div>
              <p className="text-xl font-bold text-gray-900">장바구니에 담았습니다!</p>
            </div>
          </div>
        )}
      </div>

      {/* 결제 방법 선택 모달 */}
      {showPaymentSelect && (
        <PaymentSelectModal
          product={{
            id: product.id,
            name: product.name,
            price: product.price,
            originalPrice: product.originalPrice,
            image: product.image,
            category: product.category,
          }}
          quantity={quantity}
          onClose={() => setShowPaymentSelect(false)}
        />
      )}
    </div>
  );
}
